import { IssueStatus } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';

/**
 * Customer-facing labels for the CRM issue workflow (PD-13773) — the
 * internal "To-Do" reads as "Open" to a customer, and "Done" as
 * "Resolved". Unknown / custom statuses fall through unmapped.
 */
function statusLabel(status: string): string {
	switch (status as IssueStatus) {
		case IssueStatus.TODO:
			return 'Open';
		case IssueStatus.IN_PROGRESS:
			return 'In Progress';
		case IssueStatus.DONE:
			return 'Resolved';
		default:
			return status;
	}
}

/** Pill colors matching the storefront's transaction-status treatment. */
function statusClasses(status: string): string {
	switch (status as IssueStatus) {
		case IssueStatus.TODO:
			return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
		case IssueStatus.IN_PROGRESS:
			return 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300';
		case IssueStatus.DONE:
			return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
		default:
			return 'bg-gray-100 dark:bg-gray-700/40 text-gray-700 dark:text-gray-300';
	}
}

/**
 * Status pill for a support request (PD-13773) — shared by the Support
 * list and the issue-thread page so the two always agree.
 */
export function IssueStatusChip({ status }: { status: string }): JSX.Element {
	const { t } = useTranslation();

	return (
		<span
			className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusClasses(status)}`}
		>
			{t(statusLabel(status))}
		</span>
	);
}
